import React from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { functions } from '../functions/_functions';
import { withTheme } from '../core/theming';

import Div from './Div';
import Icon from './Icon';
import Text from './Text';

const MenuItem = styled.li`
  display: list-item;
  margin: 0;
  padding: 0;
  white-space: nowrap;
`;

const MenuLink = withTheme(styled(Link)`
  cursor: pointer;
  text-decoration: none !important;
  margin: 0;
  display: flex;
  position: absolute;
  background-color: transparent;
  align-items: center;
  top: ${(props) => props.top};
  ${(props) => props.textSize && functions.makeResponsiveFontSize(props.textSize, props.theme.textSize)};
  transition: ${(props) => props.theme.transition};
  ${(props) =>
    props.highlighted
      ? props.hoverBg && functions.makeResponsiveProperty(props.hoverBg, 'background', props.theme.colors)
      : props.bg && functions.makeResponsiveProperty(props.bg, 'background', props.theme.colors)};
  ${(props) =>
    props.highlighted
      ? props.hoverTextColor && functions.makeResponsiveProperty(props.hoverTextColor, 'color', props.theme.colors)
      : props.textColor && functions.makeResponsiveProperty(props.textColor, 'color', props.theme.colors)};
  ${(props) =>
    props.highlighted
      ? functions.makeResponsive(
          'width',
          props.mini
            ? `calc(${props.theme.application.miniMenuWidth} + ${props.theme.application.menuAdditionalWidth})`
            : `calc(${props.theme.application.menuWidth} + ${props.theme.application.menuAdditionalWidth})`
        )
      : functions.makeResponsive(
          'width',
          props.mini ? props.theme.application.miniMenuWidth : props.theme.application.menuWidth
        )};
  ${(props) =>
    props.highlighted
      ? functions.makeResponsive('border-radius', '0 4px 4px 0')
      : functions.makeResponsive('border-radius', '0px')};

  &:active,
  &:focus,
  &:hover {
    outline: none !important;
    text-decoration: none !important;
    ${(props) =>
      props.hoverTextColor && functions.makeResponsiveProperty(props.hoverTextColor, 'color', props.theme.colors)};
  }

  svg {
    path {
      transition: ${(props) => props.theme.transition};
      fill: ${(props) =>
        props.highlighted ? props.theme.colors[props.hoverTextColor] : props.theme.colors[props.textColor]};
    }
    circle {
      transition: ${(props) => props.theme.transition};
      fill: ${(props) =>
        props.highlighted ? props.theme.colors[props.hoverTextColor] : props.theme.colors[props.textColor]};
    }
  }

  :hover svg {
    path {
      fill: ${(props) => props.theme.colors[props.hoverTextColor]};
    }
    circle {
      fill: ${(props) => props.theme.colors[props.hoverTextColor]};
    }
  }

  & Div {
    min-width: 7px;
    width: 7px;
    height: ${(props) => props.theme.application.menuHeight};
    ${() => functions.findDirection('border', { t: '1px solid' })};
    ${(props) =>
      props.highlighted
        ? functions.makeResponsive('border-color', props.theme.colors[props.hoverBg])
        : functions.makeResponsive('border-color', props.theme.colors[props.bg])};
    ${(props) => props.hoverBg && functions.makeResponsiveProperty(props.hoverBg, 'background', props.theme.colors)}
  }
`);

const Options = withTheme(styled.ul`
  list-style: none;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    overflow: visible;
  }
  ${(props) => props.p && functions.findDirection('padding', props.p)};
  ${(props) => props.m && functions.findDirection('margin', props.m)};
  ${(props) => props.textColor && functions.makeResponsive('color', props.theme.colors[props.textColor])};
`);

const Menu = ({
  mini,
  menuStyle,
  optionsStyle,
  itemsStyle,
  logo,
  logoStyle,
  iconsStyle,
  options,
  theme,
  footerLogo,
  footerStyle,
  isHighlighted,
}) => {
  const location = useLocation();
  return (
    <Div
      top="0"
      left="0"
      minH="100%"
      h="100%"
      minW={mini ? theme.application.miniMenuWidth : theme.application.menuWidth}
      w={mini ? theme.application.miniMenuWidth : theme.application.menuWidth}
      transition={theme.application.menuTransition}
      {...menuStyle}
    >
      <Options {...optionsStyle}>
        <li>
          <Div
            minW={mini ? theme.application.miniMenuWidth : theme.application.menuWidth}
            w={mini ? theme.application.miniMenuWidth : theme.application.menuWidth}
            minH={theme.application.headerHeight}
            h={theme.application.headerHeight}
            d="inline-flex"
            justify="center"
            align="center"
            {...logoStyle}
          >
            <Div tag="span">{mini ? logo.smallLogo : logo.largeLogo}</Div>
          </Div>
        </li>
        {options.map(
          (option, index) =>
            ({
              ITEM: (
                <MenuItem>
                  {index === 0 ? (
                    <Div
                      w={mini ? theme.application.miniMenuWidth : theme.application.menuWidth}
                      h="1px"
                      bg="black"
                      pos="absolute"
                      top={`calc(${theme.application.headerHeight} + 0px)`}
                    />
                  ) : null}
                  <MenuLink
                    highlighted={
                      option.isHighlighted
                        ? option.isHighlighted(location.pathname, option.link)
                        : isHighlighted(location.pathname, option.link)
                    }
                    mini={mini}
                    {...itemsStyle}
                    hoverBg={option.color}
                    top={`calc(${theme.application.headerHeight} + (${index} * (${theme.application.menuHeight} + 1px) + 1px))`}
                    to={option.link}
                  >
                    <Div />
                    <Icon name={option.icon} viewBox={option.iconViewBox} {...iconsStyle} {...option.iconsStyle} />
                    {!mini ? <Text textSize="body">{option.name}</Text> : null}
                  </MenuLink>
                  <Div
                    w={mini ? theme.application.miniMenuWidth : theme.application.menuWidth}
                    h="1px"
                    bg="black"
                    pos="absolute"
                    top={`calc(${theme.application.headerHeight} + (${index} * (${theme.application.menuHeight} + 1px)) + ${theme.application.menuHeight})`}
                  />
                </MenuItem>
              ),
            })[option.type]
        )}
      </Options>
      <Div
        bottom="0"
        left="0"
        w="100%"
        textColor="white"
        d="inline-flex"
        justify="center"
        align="center"
        {...{
          ...menuStyle,
          ...footerStyle,
          zIndex: (menuStyle.zIndex || 10) + 1,
        }}
      >
        {footerLogo}
      </Div>
    </Div>
  );
};

Menu.propTypes = {
  theme: PropTypes.shape({
    application: {},
  }).isRequired,
  mini: PropTypes.bool,
  menuStyle: PropTypes.shape({
    zIndex: PropTypes.number,
  }),
  optionsStyle: PropTypes.shape({}),
  iconsStyle: PropTypes.shape({}),
  itemsStyle: PropTypes.shape({}),
  logoStyle: PropTypes.shape({}),
  logo: PropTypes.shape({
    largeLogo: PropTypes.node,
    smallLogo: PropTypes.node,
  }),
  footerLogo: PropTypes.node,
  footerStyle: PropTypes.shape({}),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.oneOf(['HEADER', 'ITEM']),
      icon: PropTypes.string,
      link: PropTypes.string,
      iconsStyle: PropTypes.shape({}),
    })
  ),
  isHighlighted: PropTypes.func,
};

Menu.defaultProps = {
  mini: false,
  menuStyle: {
    pos: 'absolute',
    bg: 'shade33',
    zIndex: 10,
  },
  optionsStyle: {
    textColor: 'white',
    p: { y: '1px', x: '0px' },
  },
  logoStyle: {
    textColor: 'shade99',
    bg: 'shade33',
  },
  logo: {
    largeLogo: 'Onpoint',
    smallLogo: 'OP',
  },
  itemsStyle: {
    textColor: 'shade99',
    hoverTextColor: 'white',
    bg: 'shade33',
  },
  iconsStyle: {
    p: { l: '13px', r: '16px' },
    size: '18px',
  },
  footerLogo: null,
  footerStyle: {},
  options: [],
  isHighlighted: (pathname, link) => pathname === link,
};

export default withTheme(Menu);
